<template>
  <div v-if="$page" id="page-home">
    <header ref="header" class="header -home">
      <figure v-scroll:parallax="{ refNode: $refs.header, ratio: 0.15 }" class="header__background">
        <Image :source="$page.headerImg" />
      </figure>

      <div class="header__content">
        <div v-scroll:reveal="{ delay: 550 }" class="header__legend">
          <h1>{{ $page.title }}</h1>

          <LangRouterLink :to="{ name: 'home', hash: '#intro' }" class="header__scroller">
            <Icon id="arrow" />
          </LangRouterLink>
        </div>

        <h2 v-scroll:reveal="{ delay: 600 }" class="header__title">{{ $page.headerTitleTop }}</h2>
        <h3 v-scroll:reveal="{ delay: 650 }" class="header__title">
          {{ $page.headerTitleBottom }}
        </h3>

        <LangRouterLink
          v-if="$page.headerCtaLabel"
          v-scroll:reveal="{ delay: 700 }"
          :to="{ name: 'contact' }"
          class="button section__button -secondary -link header__cta"
        >
          {{ $page.headerCtaLabel }}
        </LangRouterLink>
      </div>

      <ul class="header__grid">
        <li class="header__grid-item">
          <h5 class="header__grid-legend">{{ $page.headerGridOneTitle }}</h5>
          <p>{{ $page.headerGridOneText }}</p>

          <LangRouterLink :to="{ name: 'about' }" class="arrow-link header__grid-link">
            <Icon id="arrow" />
            {{ $page.headerGridOneLinkLabel }}
          </LangRouterLink>
        </li>

        <li class="header__grid-item">
          <h5 class="header__grid-legend">{{ $page.headerGridTwoTitle }}</h5>
          <p>{{ $page.headerGridTwoText }}</p>

          <LangRouterLink :to="{ name: 'product.index' }" class="arrow-link header__grid-link">
            <Icon id="arrow" />
            {{ $page.headerGridTwoLinkLabel }}
          </LangRouterLink>
        </li>

        <li class="header__grid-item">
          <h5 class="header__grid-legend">{{ $page.headerGridThreeTitle }}</h5>
          <p>{{ $page.headerGridThreeText }}</p>

          <LangRouterLink :to="{ name: 'careers' }" class="arrow-link header__grid-link">
            <Icon id="arrow" />
            {{ $page.headerGridThreeLinkLabel }}
          </LangRouterLink>
        </li>
      </ul>
    </header>

    <section
      v-if="$beet.options?.banner?.message && $i18n.locale === 'en'"
      class="section -centered -alt"
    >
      <h2 v-scroll:reveal class="section__legend">
        {{ formatDate($beet.options.banner.published_at) }}
      </h2>
      <div style="font-size: 6rem; margin-top: 1rem">
        <Icon id="exclamation" />
      </div>
      <RawHtml :html="$beet.options.banner.message" />
      <br />
      <div class="row -center">
        <a
          v-if="$beet.options.banner.label1"
          :href="$beet.options.banner.url1"
          target="_blank"
          class="button -border -link"
          style="margin: 0.5rem"
        >
          {{ $beet.options.banner.label1 }}</a
        >
        <a
          v-if="$beet.options.banner.label2"
          :href="$beet.options.banner.url2"
          target="_blank"
          class="button -border -link"
          style="margin: 0.5rem"
        >
          {{ $beet.options.banner.label2 }}</a
        >
      </div>
    </section>

    <section
      v-if="$page.introStats && $page.introStats.length"
      id="overview"
      class="section -indented"
    >
      <h2 class="section__legend">{{ $page.introStatsLegend }}</h2>

      <div class="section__content">
        <h3 class="section__title">{{ $page.introStatsTitle }}</h3>
        <div class="row">
          <p class="row__item -span-4-6">{{ $page.introStatsText }}</p>

          <ul class="row row__item -center -tablet">
            <li
              v-for="({ label, value, suffix = '' }, idx) in $page.introStats"
              :key="label"
              v-scroll:reveal="{ delay: 650 + 75 * idx, offset: 0 }"
              class="stat"
            >
              <output class="stat__value">{{ value }} {{ suffix }}</output>
              <p>{{ label }}</p>
            </li>
          </ul>
        </div>
      </div>
    </section>

    <section id="intro" class="section">
      <h2 v-scroll:reveal class="section__legend">{{ $page.introLegend }}</h2>

      <header class="section__hgroup">
        <h3 v-scroll:reveal="{ delay: 100 }" class="section__title">
          {{ $page.introTitle }}
        </h3>

        <LangRouterLink
          v-scroll:reveal="{ delay: 125 }"
          :to="{ name: 'product.index' }"
          class="button -link"
          >{{ $page.introLinkLabel }}
        </LangRouterLink>
      </header>

      <div v-if="$store.getters.products.length" class="row -spread -mobile -overflow">
        <article
          v-for="(product, idx) in $store.getters.five_products"
          :key="product.id"
          v-scroll:reveal="{ delay: idx * 75 }"
          class="mini-card -overflow"
          :style="`background-image: url('${product.image.original}');`"
        >
          <span class="mini-card__label">{{ product.name }}</span>

          <LangRouterLink
            :to="{ name: 'product.index', params: { slug: product.slug } }"
            class="arrow-link mini-card__link"
          >
            <Icon id="arrow" />
            {{ $t('see_more') }}
          </LangRouterLink>
        </article>
      </div>

      <div class="section__content -push row">
        <p class="row__item section__content-line -left">{{ $page.introProductsText }}</p>

        <div class="row__item -skip-1">
          <LangRouterLink
            v-scroll:reveal="{ delay: 125 }"
            :to="{ name: 'product.index' }"
            class="button -link -border -mobile-push"
            >{{ $page.introLinkLabel }}
          </LangRouterLink>
          <a
            v-if="solutionLink"
            :href="solutionLink"
            target="_blank"
            rel="noopener"
            class="button -link -push"
          >
            {{ $page.introSolutionLabel }}
          </a>
        </div>
      </div>
    </section>

    <section
      id="geo"
      class="section -centered -cta"
      v-if="
        $page.geoBackgroundVideo.original &&
        $page.geoImg &&
        $page.geoCtaSection[0] &&
        this.$i18n.locale === 'en'
      "
    >
      <div class="section__background-media-wrap">
        <video class="section__background-media" autoplay muted loop playsinline>
          <source :src="$page.geoBackgroundVideo.original" type="video/mp4" />
        </video>
      </div>

      <div class="section__content">
        <figure v-scroll:reveal>
          <Image :source="$page.geoImg" style="max-height: 60px" />
        </figure>

        <h2 v-scroll:reveal class="section__title">
          {{ $page.geoCtaSection[0].title }}
        </h2>
        <p v-scroll:reveal="{ delay: 75 }" class="section__content-line -left">
          {{ $page.geoCtaSection[0].text }}
        </p>
        <a
          v-scroll:reveal="{ delay: 150 }"
          class="button -link -secondary -inline-mobile -push"
          :href="$page.geoCtaSection[0].buttonUrl"
          target="_blank"
          rel="noopener"
        >
          {{ $page.geoCtaSection[0].buttonText }}
        </a>
      </div>
    </section>

    <section class="section -split -alt">
      <Image v-scroll:reveal :source="$page.careerOneImg" class="section__image" />

      <div class="section__content">
        <h2 v-scroll:reveal="{ delay: 100 }" class="section__legend">
          {{ $page.careerOneLegend }}
        </h2>
        <h3 v-scroll:reveal="{ delay: 150 }" class="section__title">{{ $page.careerOneTitle }}</h3>
        <p v-scroll:reveal="{ delay: 100 }" class="section__content-line">
          {{ $page.careerOneText }}
        </p>

        <a
          v-scroll:reveal="{ delay: 150 }"
          :href="$beet.options.businessInfos.applyUrl"
          target="_blank"
          rel="noopener"
          class="button -link section__button"
        >
          {{ $page.careerOneLinkLabel }}
        </a>
      </div>
    </section>

    <section class="section -split -alt">
      <Image v-scroll:reveal :source="$page.careerTwoImg" class="section__image" />

      <div class="section__content">
        <h2 v-scroll:reveal="{ delay: 100 }" class="section__legend">
          {{ $page.careerTwoLegend }}
        </h2>
        <h3 v-scroll:reveal="{ delay: 150 }" class="section__title">{{ $page.careerTwoTitle }}</h3>
        <p v-scroll:reveal="{ delay: 100 }" class="section__content-line -left">
          {{ $page.careerTwoText }}
        </p>

        <LangRouterLink
          v-scroll:reveal="{ delay: 150 }"
          :to="{ name: 'contact' }"
          class="button -link section__button"
        >
          {{ $page.careerTwoLinkLabel }}
        </LangRouterLink>
      </div>
    </section>

    <section class="section -centered -alt">
      <h2 v-scroll:reveal class="section__legend">{{ $page.careerCtaLegend }}</h2>
      <h3 v-scroll:reveal="{ delay: 75 }" class="section__title">{{ $page.careerCtaTitle }}</h3>

      <LangRouterLink
        v-scroll:reveal="{ delay: 75 }"
        :to="{ name: 'careers' }"
        class="button -border -link section__cta"
      >
        {{ $page.careerCtaLinkLabel }}
      </LangRouterLink>
    </section>

    <section class="section -indented -cta">
      <h2 class="section__legend">{{ $page.safetyLegend }}</h2>

      <Image :source="$page.safetyImg" class="section__image" />

      <div class="section__content -narrow">
        <h3 class="section__title">{{ $page.safetyTitle }}</h3>
        <p>{{ $page.safetyText }}</p>
        <LangRouterLink
          :to="{ name: 'careers', hash: '#culture' }"
          class="button -secondary -link section__button"
          >{{ $page.safetyLinkLabel }}
        </LangRouterLink>

        <a
          v-if="policyLink"
          :href="policyLink"
          target="_blank"
          rel="noopener"
          class="button -border -alt -link section__button"
        >
          {{ $page.policyLinkLabel }}
        </a>
      </div>
    </section>

    <section v-if="$store.getters.posts.length" class="section -centered -alt">
      <h2 v-scroll:reveal class="section__legend">{{ $t('post.latest.legend') }}</h2>
      <h3 v-scroll:reveal="{ delay: 75 }" class="section__title">
        {{ $t('post.latest.title') }}
      </h3>
      <div class="section__latest-articles">
        <div
          v-for="post in $store.getters.latest_posts"
          :key="`post-${post.id}-card`"
          class="section__single-article"
        >
          <PostCard :post="post" class="card-article -latest-articles" />
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import scholarshipLogo from '@/assets/images/scholarship-logo.png'
import PostCard from '@/components/cards/PostCard.vue'
import Icon from '@/components/global/Icon.vue'
import Image from '@/components/global/Image.vue'
import LangRouterLink from '@/components/global/LangRouterLink.vue'
import RawHtml from '@/components/global/RawHtml.vue'
import { formatDate } from '@/helpers/dateHelpers.js'
import PageMetaMixin from '@/mixins/PageMetaMixin.js'

export default {
  name: 'Home',
  beetPage: 'home',
  mixins: [PageMetaMixin],
  components: {
    Image,
    Icon,
    PostCard,
    LangRouterLink,
    RawHtml,
  },
  setup: () => ({ formatDate, scholarshipLogo }),
  computed: {
    policyLink() {
      if (!this.$page.safetyPolicies || !Array.isArray(this.$page.safetyPolicies)) {
        return null
      }

      return this.$page.safetyPolicies.find((policy) =>
        policy.data?.locales?.includes(this.$i18n.locale),
      )?.original
    },
    solutionLink() {
      if (!this.$page.solutionPdf || !Array.isArray(this.$page.solutionPdf)) {
        return null
      }

      return this.$page.solutionPdf.find((solution) =>
        solution.data?.locales?.includes(this.$i18n.locale),
      )?.original
    },
  },
  mounted() {
    this.$store.dispatch('loadPosts')
    this.$store.dispatch('loadProducts')
  },
}
</script>
